import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { WifiOff, RotateCcw, FlaskConical } from "lucide-react";
import { DashboardNav } from "@/components/dashboard/DashboardNav";
import { LocationSelector, LocationDetails } from "@/components/dashboard/LocationSelector";
import { OrchardMap } from "@/components/OrchardMap";
import { MapLayerPanel } from "@/components/dashboard/MapLayerPanel";
import { SeasonOverview } from "@/components/dashboard/SeasonOverview";
import { ConfidencePanel } from "@/components/dashboard/ConfidencePanel";
import { HistoricalChart } from "@/components/dashboard/HistoricalChart";
import { ForecastSection } from "@/components/dashboard/ForecastSection";
import { AboutSection } from "@/components/dashboard/AboutSection";
import { ErrorState } from "@/components/common/States";
import { Footer } from "@/components/landing/Footer";
import { useLiveDataSync, useStations, useOrchardSummary, useOrchardMap } from "@/hooks/useOrchardData";
import { useDataMode } from "@/context/DataModeContext";
import { STATIC_STATION_VMS } from "@/data/stations";
import { longDate } from "@/lib/format";

const DEFAULT_STATION = 6;

const initialSelection = (params) => {
  const raw = (params.get("station") || "").toLowerCase();
  const match = STATIC_STATION_VMS.find((s) => String(s.id) === raw || s.slug === raw);
  return { type: "station", stationId: match ? match.id : DEFAULT_STATION };
};

const OverviewHeading = ({ location, outOfCoverage, issueDate }) => (
  <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
    <div>
      <p className="eyebrow text-[#183F32]/70" data-testid="overview-asof">Season overview{issueDate ? ` · observations through ${longDate(issueDate)}` : ""}</p>
      <h1 className="mt-3 text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-[#202522]" data-testid="overview-location-title">
        {outOfCoverage ? "Outside coverage" : location ? (location.kind === "station" ? location.name : "Your orchard") : "Loading…"}
        <span className="text-[#202522]/30">.</span>
      </h1>
    </div>
    <p className="text-sm text-[#202522]/55 max-w-sm">Select a CIMIS station or drop a pin on the map. Every figure below updates for that location.</p>
  </div>
);

const ApiUnavailable = ({ message, onRetry, onDemo }) => (
  <div role="alert" data-testid="api-unavailable" className="card-surface mt-12 p-8 sm:p-12 flex flex-col sm:flex-row sm:items-center gap-6">
    <div className="h-12 w-12 shrink-0 rounded-2xl bg-[#a1452f]/10 text-[#a1452f] grid place-items-center">
      <WifiOff size={20} strokeWidth={1.8} />
    </div>
    <div className="flex-1">
      <h2 className="text-xl font-semibold">The OrchardIQ API is unavailable</h2>
      <p className="mt-1 text-sm text-[#202522]/60">{message} No data is shown in place of the real results.</p>
    </div>
    <div className="flex flex-wrap gap-2">
      <button type="button" onClick={onRetry} data-testid="api-unavailable-retry" className="inline-flex items-center gap-2 rounded-full bg-[#183F32] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#0f2c22] transition-colors">
        <RotateCcw size={14} /> Retry
      </button>
      <button type="button" onClick={onDemo} data-testid="api-unavailable-demo" className="inline-flex items-center gap-2 rounded-full border border-[#183F32] text-[#183F32] px-5 py-2.5 text-sm font-medium hover:bg-[#183F32] hover:text-white transition-colors">
        <FlaskConical size={14} /> View illustrative demo
      </button>
    </div>
  </div>
);

const useMapLayer = () => {
  const [metric, setMetric] = useState("gdd");
  const [value, setValue] = useState("next7");
  const q = useOrchardMap(metric, value);
  return { metric, value, setMetric, setValue, q };
};

export default function Dashboard() {
  const { mode, setMode } = useDataMode();
  const [params, setParams] = useSearchParams();
  const [selection, setSelection] = useState(() => initialSelection(params));
  const [locMode, setLocMode] = useState(selection.type);
  const health = useLiveDataSync();
  const stations = useStations();
  const summary = useOrchardSummary(selection, stations.data);
  const layer = useMapLayer();
  const pin = selection.type === "custom" ? { lat: selection.lat, lng: selection.lng } : null;
  const live = mode === "live";
  const apiDown = live && (health.isError || stations.isError);

  const selectStation = (id) => {
    setSelection({ type: "station", stationId: id });
    setLocMode("station");
    setParams({ station: String(id) }, { replace: true });
    toast.success(`${stations.data?.find((s) => s.id === id)?.name || "Station"} selected`);
  };
  const dropPin = (lat, lng) => {
    const la = Math.round(lat * 10000) / 10000;
    const ln = Math.round(lng * 10000) / 10000;
    setSelection({ type: "custom", lat: la, lng: ln });
    setLocMode("custom");
    setParams({}, { replace: true });
    toast(`Pin placed at ${la.toFixed(4)}, ${ln.toFixed(4)}`, { description: live ? "Requesting estimates for this point." : "Illustrative estimate (demo mode)." });
  };
  const coverageError = summary.error?.name === "OutOfCoverageError";
  const summaryError = summary.isError;

  return (
    <div className="min-h-screen bg-[#F7F7F2] text-[#202522]" data-testid="dashboard-page">
      <DashboardNav />
      <main className="mx-auto max-w-[1440px] px-5 sm:px-8">
        {apiDown ? (
          <ApiUnavailable message={(health.error || stations.error)?.message} onRetry={() => { health.refetch(); stations.refetch(); }} onDemo={() => setMode("demo")} />
        ) : (
          <>
            <section id="overview" className="scroll-mt-32 pt-12 sm:pt-16 pb-20" data-testid="section-overview">
              <OverviewHeading location={summary.data?.location} outOfCoverage={coverageError} issueDate={summary.data?.metrics.gdd.issueDate} />
              <div className="grid gap-5 lg:grid-cols-12">
                <div className="lg:col-span-5 xl:col-span-4">
                  <LocationSelector mode={locMode} onModeChange={setLocMode} stations={stations.data} selection={selection} pin={pin} onSelectStation={selectStation} onCoords={dropPin}>
                    {summary.data && <LocationDetails location={summary.data.location} />}
                  </LocationSelector>
                </div>
                <div className="lg:col-span-7 xl:col-span-8 relative">
                  <OrchardMap
                    className="h-[520px] sm:h-[560px] lg:h-full lg:min-h-[680px] rounded-3xl shadow-[0_30px_60px_-30px_rgba(0,0,0,0.5)]"
                    stations={stations.data || []}
                    activeStationId={selection.type === "station" ? selection.stationId : null}
                    pin={pin}
                    grid={live && !layer.q.isError ? layer.q.data : null}
                    onStationSelect={selectStation}
                    onDropPin={(ll) => dropPin(ll.lat, ll.lng)}
                    testId="dashboard-map"
                  />
                  <MapLayerPanel metric={layer.metric} value={layer.value} onMetric={layer.setMetric} onValue={layer.setValue} grid={layer.q.data} isLoading={layer.q.isFetching} isError={layer.q.isError} mode={mode} />
                  <div className="pointer-events-none absolute left-4 bottom-4 z-[500] glass-dark rounded-full px-4 py-2 text-[12px] text-white/80 border border-white/10">
                    Click the map to drop a pin · select a station dot
                  </div>
                </div>
              </div>
              <div className="mt-5 space-y-5">
                {summaryError ? (
                  <ErrorState
                    testId={coverageError ? "coverage-error" : "summary-error"}
                    title={coverageError ? "Outside coverage" : "Couldn't load orchard data"}
                    message={coverageError ? `${summary.error.message} Choose a site in California's Central Valley.` : summary.error.message}
                    actionLabel={coverageError ? "Return to Davis" : "Try again"}
                    onAction={coverageError ? () => selectStation(DEFAULT_STATION) : () => summary.refetch()}
                  />
                ) : (
                  <>
                    <SeasonOverview data={summary.data} isLoading={summary.isLoading || !summary.data} />
                    <ConfidencePanel data={summary.data} isLoading={summary.isLoading || !summary.data} mode={mode} />
                  </>
                )}
              </div>
            </section>
            <section id="historical" className="scroll-mt-32 py-20 border-t border-[#183F32]/[0.08]" data-testid="section-historical">
              {!coverageError && <HistoricalChart selection={selection} />}
            </section>
            <section id="forecast" className="scroll-mt-32 py-20 border-t border-[#183F32]/[0.08]" data-testid="section-forecast">
              {!coverageError && <ForecastSection selection={selection} summary={summary} />}
            </section>
          </>
        )}
        <section id="about" className="scroll-mt-32 py-20 border-t border-[#183F32]/[0.08]" data-testid="section-about">
          <AboutSection />
        </section>
      </main>
      <Footer />
    </div>
  );
}
